import { useCallback, useMemo, memo, Fragment, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { MAP_TILE_CONFIG } from "@racoon/lib";
import {
  makeTruckIcon,
  makePickupLocationIcon,
  makeDisposalSiteIcon,
  makeDepotIcon,
  makeSuspiciousLocationIcon,
  getPickupLocationStatus,
  getPickupStatusColor,
} from "~/shared/utils";
import { ConnectProto } from "@racoon/api";
import type { TruckProviderGroup } from "../lib/use-trucks-by-provider.hook";
import { getProviderColor } from "../lib/use-trucks-by-provider.hook";

type PickupLocation = ConnectProto.PickupLocation;
type GovTruckInfo = ConnectProto.GovTruckInfo;
type Location = ConnectProto.Location;
type GovDepotInfo = ConnectProto.GovDepotInfo;
type SuspiciousLocation = ConnectProto.SuspiciousLocation;

/** Pickup location enriched client-side with the jobs (and provider) that belong to it. */
export type EnrichedPickupLocation = PickupLocation & {
  _jobs: Array<{ status?: ConnectProto.JobStatus } & Record<string, any>>;
  _serviceProvider: any;
};

type PickupMarkerOptions = L.MarkerOptions & {
  /** Representative job status, carried onto the Leaflet marker for cluster aggregation. */
  _jobStatus: ConnectProto.JobStatus;
};

type SelectionState =
  | { type: "none" }
  | { type: "truck"; truckId: string | number }
  | { type: "pickup"; data: ConnectProto.PickupLocation }
  | { type: "disposalSite"; data: Location }
  | { type: "depot"; data: GovDepotInfo }
  | { type: "suspiciousLocation"; data: SuspiciousLocation };

/**
 * A request to recenter the map. Identity matters, not value: re-selecting the
 * same place must fly again, so callers pass a fresh object each time.
 */
export type FlyToRequest = { lat: number; lon: number };

export interface DashboardMapProps {
  pickupLocations: EnrichedPickupLocation[];
  trucks: GovTruckInfo[];
  /** Ids of the trucks in `trucks` that are government-owned (drawn with their own icon). */
  governmentTruckIds?: Set<string>;
  disposalSites?: Location[];
  depots?: GovDepotInfo[];
  suspiciousLocations?: SuspiciousLocation[];
  selection: SelectionState;
  onPickupClick: (loc: EnrichedPickupLocation) => void;
  onTruckClick: (truck: GovTruckInfo) => void;
  onDisposalSiteClick?: (site: Location) => void;
  onDepotClick?: (depot: GovDepotInfo) => void;
  onSuspiciousLocationClick?: (location: SuspiciousLocation) => void;
  showTrucks: boolean;
  truckProviderGroups?: TruckProviderGroup[];
  /** Latest recenter request, e.g. from picking a map-search result. */
  flyTo?: FlyToRequest | null;
  children?: React.ReactNode;
}

// SVG donut chart, one segment per job-status bucket
// (completed = green, pending = blue, not completed = red, unknown = gray)
const createClusterIconSvg = (cluster: any): string => {
  const childMarkers = cluster.getAllChildMarkers();
  const count = childMarkers.length;

  // Aggregate representative job statuses carried on the child marker options,
  // collapsing each into the same buckets the individual markers use.
  const bucketOrder = ["completed", "pending", "notCompleted", "unknown"] as const;
  const buckets: Record<(typeof bucketOrder)[number], number> = {
    completed: 0,
    pending: 0,
    notCompleted: 0,
    unknown: 0,
  };
  for (const marker of childMarkers) {
    const status = marker.options._jobStatus as ConnectProto.JobStatus | undefined;
    switch (status) {
      case ConnectProto.JobStatus.COMPLETED:
      case ConnectProto.JobStatus.FORCE_COMPLETED:
        buckets.completed++;
        break;
      case ConnectProto.JobStatus.CREATED:
      case ConnectProto.JobStatus.ASSIGNED:
      case ConnectProto.JobStatus.ENROUTE:
      case ConnectProto.JobStatus.ON_SERVICE:
        buckets.pending++;
        break;
      case ConnectProto.JobStatus.NOT_COMPLETED:
        buckets.notCompleted++;
        break;
      default:
        buckets.unknown++;
    }
  }

  const total = count || 1;
  const size = 50;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = 18;
  const strokeWidth = 5;

  const circumference = 2 * Math.PI * radius;

  // Representative color per bucket (matches makePickupLocationIcon).
  const bucketColors: Record<(typeof bucketOrder)[number], string> = {
    completed: getPickupStatusColor(ConnectProto.JobStatus.COMPLETED),
    pending: getPickupStatusColor(ConnectProto.JobStatus.CREATED),
    notCompleted: getPickupStatusColor(ConnectProto.JobStatus.NOT_COMPLETED),
    unknown: getPickupStatusColor(ConnectProto.JobStatus.UNDEFINED),
  };

  const svgCircles = [];
  let offset = 0;
  for (const bucket of bucketOrder) {
    const segmentCount = buckets[bucket];
    if (segmentCount <= 0) continue;
    const segmentLength = (segmentCount / total) * circumference;
    svgCircles.push(`
      <circle
        cx="${centerX}"
        cy="${centerY}"
        r="${radius}"
        fill="none"
        stroke="${bucketColors[bucket]}"
        stroke-width="${strokeWidth}"
        stroke-dasharray="${segmentLength} ${circumference}"
        stroke-dashoffset="-${offset}"
        stroke-linecap="round"
      />`);
    offset += segmentLength;
  }

  const svgHtml = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="filter: drop-shadow(0 1px 3px rgba(0,0,0,0.1));">
      ${svgCircles.join("")}
      <!-- White center -->
      <circle 
        cx="${centerX}" 
        cy="${centerY}" 
        r="${radius - strokeWidth}" 
        fill="white"
        stroke="white"
        stroke-width="1"
      />
      <!-- Count -->
      <text 
        x="${centerX}" 
        y="${centerY}" 
        text-anchor="middle" 
        dominant-baseline="middle"
        font-weight="bold" 
        font-size="12"
        fill="#1f2937"
        font-family="system-ui, -apple-system, sans-serif"
      >
        ${count}
      </text>
    </svg>
  `;

  return svgHtml;
};

// Memoized truck marker component with lazy icon creation
const TruckMarkerComponent = memo(
  ({
    position,
    isSelected,
    isGovernment,
    truck,
    onClick,
    truckProviderGroups = [],
  }: {
    truckId: string;
    position: [number, number];
    isSelected: boolean;
    isGovernment: boolean;
    truck: GovTruckInfo;
    onClick: () => void;
    truckProviderGroups?: TruckProviderGroup[];
  }) => {
    // Get provider color for this truck
    const providerColor = useMemo(
      () => getProviderColor(truckProviderGroups, truck.serviceProvider?.id),
      [truck.serviceProvider?.id, truckProviderGroups],
    );

    // Create icon only on render (lazy creation)
    const icon = useMemo(
      () => makeTruckIcon(truck, { isSelected, isGovernment, providerColor }),
      [truck, isSelected, isGovernment, providerColor],
    );
    return <Marker position={position} icon={icon as L.Icon} eventHandlers={{ click: onClick }} />;
  },
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.isGovernment === next.isGovernment &&
    prev.truckId === next.truckId &&
    prev.position[0] === next.position[0] &&
    prev.position[1] === next.position[1] &&
    String(prev.truck.serviceProvider?.id) === String(next.truck.serviceProvider?.id),
);
TruckMarkerComponent.displayName = "TruckMarker";

// Memoized pickup marker; passes the representative job status via Leaflet
// marker options so the cluster donut can aggregate it.
const PickupMarkerComponent = memo(
  ({
    locId,
    position,
    isSelected,
    loc,
    jobStatus,
    onClick,
  }: {
    locId: string;
    position: [number, number];
    isSelected: boolean;
    loc: EnrichedPickupLocation;
    jobStatus: ConnectProto.JobStatus;
    onClick: () => void;
  }) => {
    // Lazy icon creation
    const icon = useMemo(() => makePickupLocationIcon(loc, { isSelected }), [loc, isSelected]);
    // Pass the job status as a custom Leaflet option for cluster aggregation
    const markerOptions = useMemo<PickupMarkerOptions>(
      () => ({ _jobStatus: jobStatus }),
      [jobStatus],
    );
    return (
      <Marker
        key={`pickup-${locId}`}
        position={position}
        icon={icon as L.Icon}
        eventHandlers={{ click: onClick }}
        {...markerOptions}
      />
    );
  },
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.locId === next.locId &&
    prev.jobStatus === next.jobStatus &&
    prev.position[0] === next.position[0] &&
    prev.position[1] === next.position[1] &&
    prev.onClick === next.onClick &&
    prev.loc === next.loc,
);
PickupMarkerComponent.displayName = "PickupMarker";

// Memoized disposal site marker
const DisposalSiteMarkerComponent = memo(
  ({
    position,
    isSelected,
    site,
    onClick,
  }: {
    siteId: string;
    position: [number, number];
    isSelected: boolean;
    site: Location;
    onClick: () => void;
  }) => {
    const icon = useMemo(() => makeDisposalSiteIcon(site, { isSelected }), [site, isSelected]);
    return <Marker position={position} icon={icon as L.Icon} eventHandlers={{ click: onClick }} />;
  },
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.siteId === next.siteId &&
    prev.position[0] === next.position[0] &&
    prev.position[1] === next.position[1],
);
DisposalSiteMarkerComponent.displayName = "DisposalSiteMarker";

// Memoized depot marker
const DepotMarkerComponent = memo(
  ({
    position,
    isSelected,
    depot,
    onClick,
  }: {
    depotId: string;
    position: [number, number];
    isSelected: boolean;
    depot: GovDepotInfo;
    onClick: () => void;
  }) => {
    const icon = useMemo(() => makeDepotIcon(depot, { isSelected }), [depot, isSelected]);
    return <Marker position={position} icon={icon as L.Icon} eventHandlers={{ click: onClick }} />;
  },
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.depotId === next.depotId &&
    prev.position[0] === next.position[0] &&
    prev.position[1] === next.position[1],
);
DepotMarkerComponent.displayName = "DepotMarker";

// Memoized suspicious location marker
const SuspiciousLocationMarkerComponent = memo(
  ({
    position,
    isSelected,
    location,
    onClick,
  }: {
    locationId: string;
    position: [number, number];
    isSelected: boolean;
    location: SuspiciousLocation;
    onClick: () => void;
  }) => {
    const icon = useMemo(
      () => makeSuspiciousLocationIcon(location, { isSelected }),
      [location, isSelected],
    );
    return <Marker position={position} icon={icon as L.Icon} eventHandlers={{ click: onClick }} />;
  },
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.locationId === next.locationId &&
    prev.position[0] === next.position[0] &&
    prev.position[1] === next.position[1],
);
SuspiciousLocationMarkerComponent.displayName = "SuspiciousLocationMarker";

// Helper to convert geometry coordinates to Leaflet format
// Supports various geometry formats from API
const convertGeometryToLatLngs = (geometry: any): [number, number][] | null => {
  if (!geometry) return null;

  // Handle GeoJSON Feature format: { type: "Feature", geometry: { type: "Polygon", coordinates: [...] } }
  let geom = geometry;
  if (geometry.type === "Feature" && geometry.geometry) {
    geom = geometry.geometry;
  }
  // Handle nested geometry format from API: { geometry: { type: "Polygon", coordinates: [...] } }
  else if (geometry.geometry && geometry.geometry.coordinates) {
    geom = geometry.geometry;
  }

  // Handle direct coordinates array
  let coords: any[] | null = null;

  if (geom.coordinates && Array.isArray(geom.coordinates)) {
    // Standard GeoJSON Polygon format: { type: "Polygon", coordinates: [[[lng, lat], ...]] }
    if (geom.type === "Polygon" || geom.type === "polygon") {
      coords = geom.coordinates[0];
    }
    // MultiPolygon format: { type: "MultiPolygon", coordinates: [[[[lng, lat], ...]]] }
    else if (geom.type === "MultiPolygon" || geom.type === "multipolygon") {
      coords = geom.coordinates[0]?.[0];
    }
    // If type is not specified, try to detect based on structure
    else if (Array.isArray(geom.coordinates[0])) {
      // Check if first element is an array of arrays (nested polygon)
      if (Array.isArray(geom.coordinates[0][0])) {
        coords = geom.coordinates[0];
      } else {
        // Direct array of [lng, lat] pairs
        coords = geom.coordinates;
      }
    }
  }
  // Handle struct format where coordinates might be directly on the object
  else if (Array.isArray(geom)) {
    coords = geom;
  }

  if (!coords || !Array.isArray(coords)) return null;

  // GeoJSON format: [lng, lat], Leaflet format: [lat, lng]
  const result = coords
    .filter(
      (c: any) =>
        Array.isArray(c) && c.length >= 2 && typeof c[0] === "number" && typeof c[1] === "number",
    )
    .map((c: any) => [c[1], c[0]] as [number, number]);

  return result.length > 0 ? result : null;
};

// Fits the map to pickup location bounds whenever the set of locations changes (initial load or filter change).
const FitBoundsOnLocationsChange = ({ locations }: { locations: EnrichedPickupLocation[] }) => {
  const map = useMap();
  const prevLocationsKey = useRef<string | null>(null);

  useEffect(() => {
    if (locations.length === 0) return;
    const locationsKey = locations.map((loc) => loc.id).join(",");
    if (prevLocationsKey.current === locationsKey) return;
    const coords = locations
      .filter((loc) => typeof loc.lat === "number" && typeof loc.lon === "number")
      .map((loc) => [loc.lat, loc.lon] as [number, number]);
    if (coords.length === 0) return;
    const isFirstLoad = prevLocationsKey.current === null;
    prevLocationsKey.current = locationsKey;
    map.fitBounds(L.latLngBounds(coords), {
      padding: [50, 50],
      animate: !isFirstLoad,
      maxZoom: 16,
    });
  }, [locations, map]);

  return null;
};

// Recenters the map whenever a new fly-to target arrives. Lives inside
// MapContainer because useMap() only works within it — the overlay `children`
// (where the search box sits) render outside and cannot call it themselves.
const FlyToTarget = ({ target }: { target: FlyToRequest | null }) => {
  const map = useMap();

  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lon], Math.max(map.getZoom(), 17), { duration: 0.8 });
  }, [target, map]);

  return null;
};

// Component to track map state and save to sessionStorage
const MapStateTracker = () => {
  const map = useMap();

  useEffect(() => {
    const handleMapChange = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      sessionStorage.setItem(
        "dashboard_map_state",
        JSON.stringify({
          lat: center.lat,
          lng: center.lng,
          zoom,
        }),
      );
    };

    // Save state on zoom/move end
    map.on("zoomend", handleMapChange);
    map.on("moveend", handleMapChange);

    return () => {
      map.off("zoomend", handleMapChange);
      map.off("moveend", handleMapChange);
    };
  }, [map]);

  return null;
};

export const DashboardMap = ({
  pickupLocations,
  trucks,
  governmentTruckIds,
  disposalSites = [],
  depots = [],
  suspiciousLocations = [],
  selection,
  onPickupClick,
  onTruckClick,
  onDisposalSiteClick,
  onDepotClick,
  onSuspiciousLocationClick,
  showTrucks,
  truckProviderGroups = [],
  flyTo = null,
  children,
}: DashboardMapProps) => {
  // Get selected IDs for quick lookup
  const selectedTruckId = selection.type === "truck" ? selection.truckId : null;
  const selectedPickupId = selection.type === "pickup" ? String(selection.data.id) : null;
  const selectedDisposalSiteId =
    selection.type === "disposalSite" ? String(selection.data.id) : null;
  const selectedDepotId = selection.type === "depot" ? String(selection.data.depot?.id) : null;
  const selectedSuspiciousLocationId =
    selection.type === "suspiciousLocation" ? String(selection.data.location?.id) : null;

  // Load initial map state from sessionStorage or use defaults
  const mapState = useMemo(() => {
    try {
      const saved = sessionStorage.getItem("dashboard_map_state");
      if (saved) {
        const state = JSON.parse(saved);
        return {
          center: [state.lat, state.lng] as [number, number],
          zoom: state.zoom,
        };
      }
    } catch {
      // Ignore parse errors
    }
    return {
      center: [43.238949, 76.889709] as [number, number],
      zoom: 13,
    };
  }, []);

  // Show all markers at small zoom (clusters will efficiently group them)
  // At large zoom still show all (Leaflet Cluster manages visibility itself)
  const visiblePickupMarkers = useMemo(
    () => pickupLocations.filter((loc: EnrichedPickupLocation) => loc.lat && loc.lon),
    [pickupLocations],
  );

  // leaflet.markercluster caches each cluster's icon (cluster._iconObj) and only
  // rebuilds it when the cluster object itself is recreated — it does NOT react to
  // the child markers' _jobStatus changing after a date change. That left cluster
  // donuts showing stale colors while the inner markers updated. Remounting the
  // whole MarkerClusterGroup via this key (positions + per-marker status) forces a
  // full re-cluster so cluster icons are recomputed from the current statuses.
  const clusterKey = useMemo(
    () => visiblePickupMarkers.map((loc) => `${loc.id}:${getPickupLocationStatus(loc)}`).join("|"),
    [visiblePickupMarkers],
  );

  const visibleTruckMarkers = useMemo(
    () => trucks.filter((truck: GovTruckInfo) => truck.lat && truck.lon),
    [trucks],
  );

  const visibleDisposalSites = useMemo(
    () => disposalSites.filter((site: Location) => site.lat && site.lon),
    [disposalSites],
  );

  const visibleDepots = useMemo(
    () => depots.filter((depot: GovDepotInfo) => depot.depot?.lat && depot.depot?.lon),
    [depots],
  );

  const visibleSuspiciousLocations = useMemo(
    () =>
      suspiciousLocations.filter(
        (location: SuspiciousLocation) =>
          typeof location.location?.lat === "number" && typeof location.location?.lon === "number",
      ),
    [suspiciousLocations],
  );

  // Custom cluster icon with segmented donut chart
  const createClusterCustomIcon = useCallback((cluster: any) => {
    const svgHtml = createClusterIconSvg(cluster);
    return L.divIcon({
      html: svgHtml,
      className: "custom-marker-cluster",
      iconSize: L.point(50, 50, true),
      iconAnchor: L.point(25, 25),
    });
  }, []);

  // Memoize callbacks for each marker
  const pickupCallbacks = useMemo(() => {
    const callbacks = new Map<string, () => void>();
    visiblePickupMarkers.forEach((loc) => {
      if (!loc || !loc.id) return;
      const locId = String(loc.id);
      if (locId && locId !== "undefined" && !callbacks.has(locId)) {
        callbacks.set(locId, () => onPickupClick(loc));
      }
    });
    return callbacks;
  }, [visiblePickupMarkers, onPickupClick]);

  const truckCallbacks = useMemo(() => {
    const callbacks = new Map<string, () => void>();
    visibleTruckMarkers.forEach((truck) => {
      if (!truck || !truck.truck?.id) return;
      const truckId = String(truck.truck.id);
      if (truckId && truckId !== "undefined" && !callbacks.has(truckId)) {
        callbacks.set(truckId, () => onTruckClick(truck));
      }
    });
    return callbacks;
  }, [visibleTruckMarkers, onTruckClick]);

  const disposalSiteCallbacks = useMemo(() => {
    const callbacks = new Map<string, () => void>();
    if (!onDisposalSiteClick) return callbacks;
    visibleDisposalSites.forEach((site) => {
      if (!site || !site.id) return;
      const siteId = String(site.id);
      if (siteId && siteId !== "undefined" && !callbacks.has(siteId)) {
        callbacks.set(siteId, () => onDisposalSiteClick(site));
      }
    });
    return callbacks;
  }, [visibleDisposalSites, onDisposalSiteClick]);

  const depotCallbacks = useMemo(() => {
    const callbacks = new Map<string, () => void>();
    if (!onDepotClick) return callbacks;
    visibleDepots.forEach((depot) => {
      if (!depot || !depot.depot?.id) return;
      const depotId = String(depot.depot.id);
      if (depotId && depotId !== "undefined" && !callbacks.has(depotId)) {
        callbacks.set(depotId, () => onDepotClick(depot));
      }
    });
    return callbacks;
  }, [visibleDepots, onDepotClick]);

  const suspiciousLocationCallbacks = useMemo(() => {
    const callbacks = new Map<string, () => void>();
    if (!onSuspiciousLocationClick) return callbacks;
    visibleSuspiciousLocations.forEach((location) => {
      if (!location || !location.location?.id) return;
      const locationId = String(location.location.id);
      if (locationId && locationId !== "undefined" && !callbacks.has(locationId)) {
        callbacks.set(locationId, () => onSuspiciousLocationClick(location));
      }
    });
    return callbacks;
  }, [visibleSuspiciousLocations, onSuspiciousLocationClick]);

  return (
    <div className="flex-1 relative min-h-0 w-full h-full lg:rounded-2xl overflow-hidden">
      <MapContainer
        center={mapState.center}
        zoom={mapState.zoom}
        scrollWheelZoom={true}
        className="h-full w-full"
        doubleClickZoom
        zoomControl={false}
        attributionControl={true}
      >
        <TileLayer url={MAP_TILE_CONFIG.url} attribution={MAP_TILE_CONFIG.attributionShort} />

        {/* Component to track and save map state */}
        <MapStateTracker />

        {/* Fits bounds whenever the pickup location set changes (filter change or initial load) */}
        <FitBoundsOnLocationsChange locations={visiblePickupMarkers} />

        {/* Recenters on the latest map-search selection */}
        <FlyToTarget target={flyTo} />

        {/* Pickup location markers */}
        <MarkerClusterGroup
          key={clusterKey}
          chunkedLoading
          maxClusterRadius={100}
          disableClusteringAtZoom={16}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          iconCreateFunction={createClusterCustomIcon}
        >
          {visiblePickupMarkers.map((loc, index) => {
            // Guard against undefined data
            if (!loc || typeof loc.lat !== "number" || typeof loc.lon !== "number") {
              return null;
            }
            const isSelected =
              selectedPickupId !== null && String(loc.id) === String(selectedPickupId);
            const locId = String(loc.id);
            const onClick = pickupCallbacks.get(locId);
            if (!onClick) return null;
            return (
              <PickupMarkerComponent
                key={`pickup-${loc.id || loc.name || index}`}
                locId={locId}
                position={[loc.lat, loc.lon]}
                isSelected={isSelected}
                loc={loc}
                jobStatus={getPickupLocationStatus(loc)}
                onClick={onClick}
              />
            );
          })}
        </MarkerClusterGroup>

        {/* Truck markers */}
        {showTrucks &&
          visibleTruckMarkers.map((truck, index) => {
            // Guard against undefined data
            if (!truck || typeof truck.lat !== "number" || typeof truck.lon !== "number") {
              return null;
            }
            const truckId = String(truck.truck?.id);
            const isSelected = selectedTruckId !== null && String(selectedTruckId) === truckId;
            const onClick = truckCallbacks.get(truckId);
            if (!onClick) return null;
            return (
              <TruckMarkerComponent
                key={`truck-${truck.truck?.id || truck.truck?.plateNumber || index}`}
                truckId={truckId}
                position={[truck.lat, truck.lon]}
                isSelected={isSelected}
                isGovernment={governmentTruckIds?.has(truckId) ?? false}
                truck={truck}
                onClick={onClick}
                truckProviderGroups={truckProviderGroups}
              />
            );
          })}

        {/* Disposal site markers with geometry */}
        {visibleDisposalSites.map((site, index) => {
          if (!site || typeof site.lat !== "number" || typeof site.lon !== "number") {
            return null;
          }
          const siteId = String(site.id);
          const isSelected =
            selectedDisposalSiteId !== null && String(selectedDisposalSiteId) === siteId;
          const onClick = disposalSiteCallbacks.get(siteId) || (() => onDisposalSiteClick?.(site));
          const geometry = convertGeometryToLatLngs(site.geometry);
          return (
            <Fragment key={`disposal-site-${site.id || index}`}>
              {/* Render polygon if geometry exists */}
              {geometry && geometry.length > 0 && site.name.toLocaleLowerCase() !== "kwc" && (
                <Polygon
                  positions={geometry}
                  pathOptions={{
                    color: "#F58743",
                    fillColor: "#FFBC13",
                    fillOpacity: 0.3,
                    weight: 2,
                  }}
                  eventHandlers={{ click: onClick }}
                />
              )}
              {geometry && geometry.length > 0 && site.name.toLocaleLowerCase() === "kwc" && (
                <Polygon
                  positions={geometry}
                  pathOptions={{
                    color: "#32ADE6",
                    fillColor: "#32ADE6",
                    fillOpacity: 0.3,
                    weight: 2,
                  }}
                  eventHandlers={{ click: onClick }}
                />
              )}
              {/* Render marker */}
              <DisposalSiteMarkerComponent
                siteId={siteId}
                position={[site.lat, site.lon]}
                isSelected={isSelected}
                site={site}
                onClick={onClick}
              />
            </Fragment>
          );
        })}

        {/* Depot markers with geometry */}
        {visibleDepots.map((depot, index) => {
          const depotLocation = depot.depot;
          if (
            !depotLocation ||
            typeof depotLocation.lat !== "number" ||
            typeof depotLocation.lon !== "number"
          ) {
            return null;
          }
          const depotId = String(depotLocation.id);
          const isSelected = selectedDepotId !== null && String(selectedDepotId) === depotId;
          const onClick = depotCallbacks.get(depotId) || (() => onDepotClick?.(depot));
          const geometry = convertGeometryToLatLngs(depotLocation.geometry);

          return (
            <Fragment key={`depot-${depotLocation.id || index}`}>
              {/* Render polygon if geometry exists */}
              {geometry && geometry.length > 0 && (
                <Polygon
                  positions={geometry}
                  pathOptions={{
                    color: "rgb(16, 86, 218)",
                    fillColor: "rgb(16, 86, 218)",
                    fillOpacity: 0.3,
                    weight: 2,
                  }}
                  eventHandlers={{ click: onClick }}
                />
              )}
              {/* Render marker */}
              <DepotMarkerComponent
                depotId={depotId}
                position={[depotLocation.lat, depotLocation.lon]}
                isSelected={isSelected}
                depot={depot}
                onClick={onClick}
              />
            </Fragment>
          );
        })}

        {/* Suspicious location markers with geometry (red geofences) */}
        {visibleSuspiciousLocations.map((location, index) => {
          const loc = location.location;
          if (!loc || typeof loc.lat !== "number" || typeof loc.lon !== "number") {
            return null;
          }
          const locationId = String(loc.id);
          const isSelected =
            selectedSuspiciousLocationId !== null &&
            String(selectedSuspiciousLocationId) === locationId;
          const onClick =
            suspiciousLocationCallbacks.get(locationId) ||
            (() => onSuspiciousLocationClick?.(location));
          const geometry = convertGeometryToLatLngs(loc.geometry);

          return (
            <Fragment key={`suspicious-location-${loc.id || index}`}>
              {/* Render red polygon (geofence) if geometry exists */}
              {geometry && geometry.length > 0 && (
                <Polygon
                  positions={geometry}
                  pathOptions={{
                    color: "#DC2626",
                    fillColor: "#DC2626",
                    fillOpacity: 0.2,
                    weight: 2,
                  }}
                  eventHandlers={{ click: onClick }}
                />
              )}
              {/* Render marker */}
              <SuspiciousLocationMarkerComponent
                locationId={locationId}
                position={[loc.lat, loc.lon]}
                isSelected={isSelected}
                location={location}
                onClick={onClick}
              />
            </Fragment>
          );
        })}
      </MapContainer>
      {children}
    </div>
  );
};
